"use client"

/**
 * Global error boundary — catches errors from the root layout itself.
 *
 * This is the last-resort error boundary. It renders when the root layout
 * (or [locale]/layout.tsx) throws during rendering — e.g., message loading
 * failure, theme resolution crash, or provider initialization error.
 *
 * Because it replaces the root layout's <html>/<body>, it must render its
 * own complete HTML document shell. No providers (next-intl, theme, auth)
 * are available — use hardcoded strings only.
 *
 * Next.js requires this to be a "use client" component.
 */

import { useEffect } from "react"
import { notifyBoundaryError } from "@/lib/bugsnag/notify"
import { recoverFromDeploySkew } from "@/lib/errors/deploy-skew"

export default function GlobalError({
  error,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  useEffect(() => {
    // Stale chunk / Server Action from a tab predating the current deploy
    // self-heals on a fresh load — auto-reload once instead of showing the error
    // screen. Falls through to the UI below if it recurs (asset truly missing).
    if (recoverFromDeploySkew(error)) return
    console.error("[GlobalError]", error)
    notifyBoundaryError("GlobalError", error)
  }, [error])

  return (
    <html lang="en">
      <body style={{ margin: 0, fontFamily: "system-ui, sans-serif" }}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
            padding: "2rem",
            textAlign: "center",
          }}
        >
          <h1 style={{ fontSize: "1.5rem", fontWeight: 600, marginBottom: "0.5rem" }}>
            Something went wrong
          </h1>
          <p style={{ color: "#6b7280", marginBottom: "1.5rem" }}>
            An unexpected error occurred. Please try again.
          </p>
          <div style={{ display: "flex", gap: "0.75rem" }}>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: "0.5rem 1.5rem",
                borderRadius: "0.375rem",
                border: "1px solid #d1d5db",
                background: "#fff",
                cursor: "pointer",
                fontSize: "0.875rem",
                fontWeight: 500,
                letterSpacing: "0.025em",
                textTransform: "uppercase",
              }}
            >
              Try again
            </button>
            {/* eslint-disable-next-line @next/next/no-html-link-for-pages -- global-error replaces the root layout; Next.js router/Link are unavailable */}
            <a
              href="/"
              style={{
                padding: "0.5rem 1.5rem",
                borderRadius: "0.375rem",
                border: "1px solid transparent",
                background: "#111",
                color: "#fff",
                textDecoration: "none",
                fontSize: "0.875rem",
                fontWeight: 500,
                letterSpacing: "0.025em",
                textTransform: "uppercase",
              }}
            >
              Go to homepage
            </a>
          </div>
          {error.digest && (
            <p style={{ marginTop: "1.5rem", fontSize: "0.75rem", color: "#9ca3af" }}>
              #{error.digest}
            </p>
          )}
        </div>
      </body>
    </html>
  )
}
